/**
 * RECENTLY OPENED — the recall workspace (#2350 §9, #2362).
 *
 * Extracted from `ShellDrawer.tsx`'s `NavPanel` rather than grown inside it,
 * which is what the design asks for: *"Extract the current NavPanel from
 * ShellDrawer.tsx into a focused RecentPinnedPanel rather than growing the
 * drawer registry further. Do not add another modal, route, rail item or
 * store."* Everything custodial is unchanged — the same `nav` panel key, the
 * same one topbar trigger (`RecentPinnedTrigger.tsx`), the same
 * `hostTriggered` registration and the same canonical destinations. Record
 * pins were retired by #2362: persistent shortcuts belong to navigation
 * destinations and render in the navbar's first Pinned section.
 *
 * What is new is the READING of them.
 *
 * ── what this panel is for, and the change log it answers ──────────────────
 * The mock's §9 lists sixteen findings against the old lens. The ones that are
 * decisions rather than polish, and where each is answered:
 *
 *   · search lived only in the topbar ⌘K — OUTSIDE the panel whose whole job is
 *     recall. It is now in here, over the finite hydrated result, issuing no
 *     request per keystroke (`matchesQuery`).
 *   · the old RECENT AND PINNED / PINNED / RECENTLY OPENED hierarchy collapsed
 *     into one title with a count; #2362 then removed record pins entirely.
 *   · the "By type" toggle existed and its grouping was never shown. There are
 *     three real modes now, and each renders group headers with live counts.
 *   · seven identical `17 Sep` stamps become relative time (`formatWhen`), with
 *     the exact instant kept in `title` for anyone who needs it.
 *   · a row said "Q8" three times and meant three different records. Each row
 *     now carries the governed subtitle as a context line, and By account
 *     carries the governed account.
 *   · nothing could be removed. Rows have Remove from recent, the footer has
 *     Clear recents, and both are undoable.
 *
 * ── the honesty rules, which are the load-bearing half ─────────────────────
 * Three things this panel must never do, each of which is one convenient line
 * away:
 *
 *   1 · **No raw identifier is ever copy.** Not a uuid, not an entity key. The
 *       type pill is the registry's `singular_label`; an unnameable account
 *       reads `Account not shown`, never its id; a present-but-unnamed record
 *       reads `Unnamed {type}`. `recallGrouping.matchesQuery` deliberately does
 *       not search `entity`/`id` either — matching on them would make the
 *       identifier part of the interface.
 *   2 · **A hidden or deleted record discloses nothing.** `recall_contexts`
 *       returns no row for one, `isListable` drops it, and the count it would
 *       have contributed to is computed from the rows that survived — so the
 *       total cannot betray a row that was filtered out.
 *   3 · **This is history, not a shortcut shelf.** Pinning lives on navigation
 *       destinations; Clear recents therefore has exactly one collection to
 *       affect.
 *
 * ── one door, and it is asked once ────────────────────────────────────────
 * `useRecallContexts` is a single `core.recall_contexts` call over recents —
 * never one request per row. Search, type filters,
 * range and grouping are all client-side over that finite result, which is why
 * typing costs nothing and why a filtered view can never be more current than
 * the list it filters.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { History, Search, X } from 'lucide-react'
import { Button, Chip, CountPill, EntityIcon, IconTile, ListRow, SearchField, SegmentedControl } from '@solum/ui'
import { useEntityIdentities, useRecallContexts } from '../data/queries'
import { isListable, recordNaming } from '../data/recordIdentity'
import { recordDestination } from './recordDestination'
import {
  clearRecents, getAllRecents, recentsReady, recordRecent, removeRecent,
  restoreRecents, useRecentsVersion, type RecentItem,
} from './recents'
import { recentsEmptyState } from './recentsEmptyState'
import {
  formatWhen, groupRows, inRange, matchesQuery, toneOf, typeChips,
  type GroupMode, type Range, type RecallRow,
} from './recallGrouping'
import s from './RecentPinnedPanel.module.css'

const MODES: { key: GroupMode; label: string }[] = [
  { key: 'latest', label: 'Latest' },
  { key: 'type', label: 'By type' },
  { key: 'account', label: 'By account' },
]

const RANGES: { key: Range; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
  { key: 'all', label: 'All time' },
]

/** What a destructive history action left behind, and how to put it back. */
interface Undoable { message: string; items: RecentItem[] }

export function RecentPinnedPanel({ closePanel }: { closePanel?: () => void }) {
  const navigate = useNavigate()
  const recentsVersion = useRecentsVersion()   // re-render on a visit, a removal or a hydration

  const [query, setQuery] = useState('')
  const [mode, setMode] = useState<GroupMode>('latest')
  const [range, setRange] = useState<Range>('all')
  const [chosenTypes, setChosenTypes] = useState<string[]>([])
  const [undoable, setUndoable] = useState<Undoable | null>(null)

  const searchRef = useRef<HTMLInputElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const rowRefs = useRef<(HTMLButtonElement | null)[]>([])

  /* `now` is frozen for the life of the panel rather than read per render.
     Recomputing it inside `bucketOf` would let a row silently change group
     mid-interaction — and a list that reorders itself while somebody is
     arrowing down it is worse than a stale bucket boundary. */
  const now = useMemo(() => new Date(), [])

  /* ── the one door ─────────────────────────────────────────────────────── */

  const rawRecents = useMemo(
    () => getAllRecents(),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- an external store, keyed by its version
    [recentsVersion],
  )
  const refs = useMemo(
    () => rawRecents.map((r) => ({ type: r.entity, id: r.id })),
    [rawRecents])

  const entities = useEntityIdentities()
  const recall = useRecallContexts(refs)

  const entityIdentity = useMemo(
    () => new Map((entities.data ?? []).map((e) => [e.entity_type as string, e])),
    [entities.data])

  const canNavigate = useCallback(
    (entity: string) => entityIdentity.get(entity)?.navigation_eligible === true,
    [entityIdentity])

  /**
   * One resolved row, or null when the door said nothing about it.
   *
   * `present`, not `resolved` (#2325 §3): the door returning a ROW means the
   * caller may read that record; whether it carries a NAME is a separate fact.
   * Filtering on the name deleted records somebody had just opened.
   */
  const resolve = useCallback((entity: string, id: string, at: string | null): RecallRow | null => {
    if (!canNavigate(entity)) return null
    const identity = entityIdentity.get(entity)
    const row = recall.byRef.get(`${entity}:${id}`)
    const typeLabel = (identity?.singular_label as string | undefined) ?? 'Record'
    const naming = recordNaming({
      row,
      entityKnown: identity !== undefined,
      titleDeclared: (identity?.title_declared as boolean | undefined) ?? false,
      typeLabel,
      pending: recall.query.isPending,
      failed: recall.query.isError,
    })
    if (!isListable(naming.state)) return null
    return {
      entity, id, at,
      title: naming.title,
      typeLabel,
      iconKey: (identity?.icon_key as string | undefined) ?? 'record',
      context: row?.subtitle ?? null,
      accountId: row?.account_id ?? null,
      accountTitle: row?.account_title ?? null,
    }
  }, [canNavigate, entityIdentity, recall.byRef, recall.query.isPending, recall.query.isError])

  /* ── recents: range, then search + types, then grouping ───────────────── */

  const resolvedRecents = useMemo(
    () => rawRecents
      .map((r) => resolve(r.entity, r.id, r.at))
      .filter((r): r is RecallRow => r !== null),
    [rawRecents, resolve])

  /* Range applies to RECENTS ONLY, and before the chips are derived, so a
     chip's count can never promise rows the range has already excluded. */
  const inWindow = useMemo(
    () => resolvedRecents.filter((r) => !r.at || inRange(r.at, now, range)),
    [resolvedRecents, now, range])

  const chips = useMemo(() => typeChips(inWindow), [inWindow])

  /* A chosen type that is no longer on offer would silently filter everything
     away — so the selection is intersected with what the chips actually show
     rather than trusted from before the range or the query changed. */
  const activeTypes = useMemo(
    () => chosenTypes.filter((t) => chips.some((c) => c.entity === t)),
    [chosenTypes, chips])

  const visibleRecents = useMemo(() => inWindow
    .filter((r) => activeTypes.length === 0 || activeTypes.includes(r.entity))
    .filter((r) => matchesQuery(r, query)),
    [inWindow, activeTypes, query])

  const groups = useMemo(
    () => groupRows(visibleRecents, mode, now),
    [visibleRecents, mode, now])

  const total = visibleRecents.length

  /* ── the states, and they are five distinct things ────────────────────── */

  const emptyState = recentsEmptyState({
    stored: rawRecents.length,
    shown: resolvedRecents.length,
    pending: recall.query.isPending,
  })
  const filteredEmpty = resolvedRecents.length > 0 && visibleRecents.length === 0
  const filtersOn = query.trim() !== '' || activeTypes.length > 0 || range !== 'all'

  /* ── actions ──────────────────────────────────────────────────────────── */

  const open = useCallback((row: RecallRow) => {
    recordRecent(row.entity, row.id)
    closePanel?.()
    void navigate(recordDestination(row.entity, row.id))
  }, [closePanel, navigate])

  const forget = useCallback((row: RecallRow) => {
    const item = rawRecents.find((r) => r.entity === row.entity && r.id === row.id)
    if (!removeRecent(row.entity, row.id)) return
    setUndoable({ message: `Removed ${row.title} from recents.`, items: item ? [item] : [] })
  }, [rawRecents])

  const forgetAll = useCallback(() => {
    const removed = clearRecents()
    if (removed.length === 0) return
    setUndoable({
      message: `Cleared ${removed.length} recent ${removed.length === 1 ? 'record' : 'records'}.`,
      items: removed,
    })
  }, [])

  const undo = useCallback(() => {
    if (undoable) restoreRecents(undoable.items)
    setUndoable(null)
  }, [undoable])

  /* ── keyboard ─────────────────────────────────────────────────────────── */

  /* The search field takes focus when the panel opens — it is the control the
     panel exists for, and the design says so.

     AFTER PAINT, and only if focus is not already inside the panel. A bare
     mount-time `focus()` was the first version, and `recall-panel.spec.ts`
     measured it as `inactive` in a real browser: the drawer moves focus to its
     own surface when it reveals a panel, and it does so after this effect has
     run. jsdom mounts this component with no drawer around it and reported the
     field focused, which is the whole argument for having a browser spec at
     all — the unit rig agreed with a claim the product did not hold.

     Two frames, not one: the first is when the drawer's own focus call lands,
     the second is where this one has to be to survive it.

     The `contains` guard is what keeps this from being focus theft. A person
     who has already tabbed to a row keeps their place; only a panel nobody is
     standing in gets its field focused.

     `preventScroll` because the drawer is a scroll container and the browser's
     own scroll-into-view fights the sticky header on the compact sheet. */
  useEffect(() => {
    let raf = 0
    const settle = () => {
      const panel = panelRef.current
      if (panel && panel.contains(document.activeElement)) return
      searchRef.current?.focus({ preventScroll: true })
    }
    raf = requestAnimationFrame(() => { raf = requestAnimationFrame(settle) })
    return () => cancelAnimationFrame(raf)
  }, [])

  const flat = visibleRecents
  const focusRow = useCallback((index: number) => {
    const target = rowRefs.current[index]
    if (target) target.focus()
    else searchRef.current?.focus()
  }, [])

  const onKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement
    const index = rowRefs.current.findIndex((el) => el === target)

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      focusRow(index < 0 ? 0 : Math.min(index + 1, flat.length - 1))
      return
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      if (index <= 0) searchRef.current?.focus()
      else focusRow(index - 1)
      return
    }
    /* Escape closes the INNERMOST thing: the undo notice first, the panel after.
       The drawer's own `closeOnEscape` handles the panel, so this only has to
       stop the event when there is something nearer to dismiss. */
    if (event.key === 'Escape' && undoable) {
      event.stopPropagation()
      setUndoable(null)
    }
  }, [flat, focusRow, undoable])

  rowRefs.current = []

  /* ── rendering ────────────────────────────────────────────────────────── */

  const row = (item: RecallRow, index: number) => {
    const when = item.at ? formatWhen(item.at, now) : null
    return (
      /* The ROW is the addressable unit and the BUTTON is what opens it —
         two ids, because a browser spec asking "how many rows" and one asking
         "what has focus" are asking about different elements. The old lens had
         neither, and `accounts.spec.ts` selected rows with `[class*=navRow]`:
         a selector bound to a class name a stylesheet owns, which is why it
         broke the moment this file existed. */
      <li key={`${item.entity}:${item.id}`} className={s.row} data-testid="recall-row">
        {/* THE KIT'S LIST ROW (#2406). This was `.rowMain`, `.rowText`,
            `.rowTitle`, `.rowContext` and `.rowWhen` — the same box and text
            block the relationship workspace drew twice more for its rail and
            its candidates. The row now owns its height, hover, focus ring and
            truncation once; what stays here is the per-entity TONE, which is
            this panel's data and not something the kit could name. */}
        <ListRow
          size="sm"
          ref={(el) => { rowRefs.current[index] = el }}
          onClick={() => open(item)}
          data-testid="recall-row-open"
          leading={(
            <IconTile className={s.tone} data-tone={toneOf(item.entity)}>
              <EntityIcon iconKey={item.iconKey} size={16} />
            </IconTile>
          )}
          label={<span data-testid="recall-row-title">{item.title}</span>}
          detail={item.context}
          trailing={(
            <>
              <span className={s.rowType} data-tone={toneOf(item.entity)} data-testid="recall-row-type">{item.typeLabel}</span>
              {when && (
                /* The short form is what is read; the exact instant stays on
                   the element, so a screen reader and a hover both reach it.
                   Its size and colour are the row's trailing metadata. */
                <span className="tnum" title={when.exact}>
                  <span aria-hidden="true">{when.text}</span>
                  <span className={s.srOnly}>{when.exact}</span>
                </span>
              )}
            </>
          )}
        />
        {/* The row action is a SIBLING of the row, never nested inside it and
            never hover-only: it must be individually reachable, and a
            hover-revealed control does not exist on a touch screen. It is the
            kit's ghost `Button` whole — its hover, its disabled state and its
            ink are the kit's; the one thing this panel adds is the 44px
            height on EVERY pointer (`.rowAction`). */}
        <Button
          size="sm" variant="ghost" className={s.rowAction}
          aria-label={`Remove ${item.title} from recents`}
          title="Remove from recents"
          disabled={!recentsReady()}
          onClick={() => forget(item)}
          data-testid="recall-forget"
          icon={<X size={15} />}
        />
      </li>
    )
  }

  let index = -1

  return (
    /* The keydown sits on the CONTAINER and needs no a11y suppression, which
       was checked rather than assumed: a disable comment was written here first
       and eslint reported it as unused. Every target inside is a real `<button>`
       with its own Enter/Space behaviour; this handler only adds roving
       arrow/`P`/Escape on top of controls that are already operable, which is
       why the rule does not fire. */
    <div ref={panelRef} className={s.panel} data-testid="nav-panel" onKeyDown={onKeyDown}>
      <header className={s.titleBar} data-testid="recall-titlebar">
        <span className={s.titleBarIcon} aria-hidden="true"><History size={15} /></span>
        <h2 className={s.titleBarName}>Recently opened</h2>
        {/* The count is a sibling of the name, not part of it: a heading whose
            accessible name changes every time a filter moves is unusable. */}
        <CountPill tone="brand" aria-hidden="true">{total}</CountPill>
        <span className={s.srOnly}>{total} records</span>
        {closePanel && (
          <Button size="sm" variant="ghost" className={s.titleBarClose}
            aria-label="Close recently opened records" onClick={closePanel}
            data-testid="recall-close" icon={<X size={15} />} />
        )}
      </header>

      {/* The kit's value slot with a glyph (#2406). This was a positioned
          wrapper, an absolutely-placed icon and a hand-derived left padding —
          the same three pieces `RelationshipWorkspace` had written twice, with
          a note saying the kit had no leading-icon primitive. It does now.
          The field also stops being an OUTLINED box and becomes the recess
          `Input` draws, which is the vocabulary Spec 2 §3 replaced it with. */}
      <SearchField
        ref={searchRef}
        icon={<Search size={14} />}
        wrapClassName={s.searchWrap}
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search recently opened"
        aria-label="Search recently opened records"
        data-testid="recall-search"
      />

      {/* ── controls ────────────────────────────────────────────────────── */}
      <div className={s.controls} data-testid="recall-controls">
        {/* THE KIT'S RADIOGROUP (#2406). The note that used to sit here said
            this had to stay raw because "the kit's Button brings its own
            padding, radius and border" — which weighed `Button`, not
            `SegmentedControl`, the control that owns exactly this shape and
            has since before this panel was written.

            The copy was not merely redundant, it was incomplete: a WAI-ARIA
            radiogroup roves with the arrow keys and answers Home and End, and
            these three buttons did none of that. The panel's own `onKeyDown`
            is on the PANEL, not on the group. So this is not a subtraction that
            costs behaviour — it is one that adds the behaviour the pattern
            always required. */}
        <SegmentedControl<GroupMode>
          size="sm"
          label="Group recents by"
          testId="recall-modes"
          value={mode}
          onChange={setMode}
          options={MODES.map((m) => ({ value: m.key, label: m.label, testId: `recall-mode-${m.key}` }))}
        />
        <label className={s.rangeWrap}>
          <span className={s.srOnly}>Show recents from</span>
          <select className={s.range} value={range} data-testid="recall-range"
            onChange={(e) => setRange(e.target.value as Range)}>
            {RANGES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
        </label>
      </div>

      {chips.length > 1 && (
        <ul className={s.chips} data-testid="recall-chips">
          {chips.map((chip) => {
            const on = activeTypes.includes(chip.entity)
            return (
              <li key={chip.entity}>
                {/* A real toggle. The count is inside the button but marked
                    `aria-hidden`, with the name carrying the type alone — a
                    control announced as "Lead 4" changes its own name every
                    time the data moves, which is the design's "counts are not
                    their accessible names". */}
                {/* THE KIT'S CHIP (#2406). The note here said "the kit has no
                    chip. Adding one is a `packages/ui` change, and
                    `feat/2345-launcher-panel` holds that tree." Both halves are
                    stale: `Chip` and `FilterChip` are exported from
                    `controls/index.ts`, and that tree landed long ago. A stale
                    reason reads exactly like a live one, which is why this note
                    is replaced rather than deleted.

                    `tone` stays a DATA attribute, not the kit's `tone` prop:
                    the kit's is a semantic status (good/warn/critical) while
                    this one is a per-entity palette index, and collapsing the
                    two would say a Lead is "good". */}
                <Chip active={on} data-tone={toneOf(chip.entity)}
                  className={s.chip} aria-label={chip.label}
                  data-testid={`recall-chip-${chip.entity}`}
                  onClick={() => setChosenTypes((prev) => prev.includes(chip.entity)
                    ? prev.filter((t) => t !== chip.entity)
                    : [...prev, chip.entity])}>
                  <EntityIcon iconKey={chip.iconKey} size={13} />
                  <span aria-hidden="true">{chip.label}</span>
                  <span className={s.chipCount} aria-hidden="true">{chip.count}</span>
                </Chip>
              </li>
            )
          })}
        </ul>
      )}

      {/* ── the rows ────────────────────────────────────────────────────── */}
      <div className={s.list} data-testid="nav-recent">
        {recall.query.isPending && rawRecents.length > 0 && (
          <p className={s.quiet} data-testid="recall-loading">Loading your recent records…</p>
        )}
        {groups.map((group) => (
          <section key={group.key} className={s.group} aria-labelledby={`recall-g-${group.key}`}>
            <h3 className={s.groupH} id={`recall-g-${group.key}`}>
              {group.label}
              <CountPill tone="outline" aria-hidden="true">{group.count}</CountPill>
            </h3>
            <ul className={s.rows}>
              {group.rows.map((item) => { index += 1; return row(item, index) })}
            </ul>
          </section>
        ))}
        {/* FIVE distinct states, and they say different things on purpose: a
            filtered-empty list is not an empty history, and neither is a
            refused read. */}
        {filteredEmpty && (
          <p className={s.quiet} data-testid="recall-filtered-empty">
            {filtersOn
              ? 'No recent records match these filters.'
              : 'No recent records in this range.'}
          </p>
        )}
        {!filteredEmpty && emptyState && (
          <p className={s.quiet} data-testid={`nav-recent-${emptyState.kind}`}>
            {emptyState.text}
            {emptyState.kind === 'unnameable' && (
              <Button size="sm" variant="ghost" data-testid="nav-recent-retry"
                onClick={() => { void recall.query.refetch(); void entities.refetch() }}>
                Try again
              </Button>
            )}
          </p>
        )}
      </div>

      {/* ── footer ──────────────────────────────────────────────────────── */}
      <footer className={s.foot} data-testid="recall-footer">
        {undoable ? (
          <div className={s.undo} role="status" data-testid="recall-undo">
            <span className={s.undoText}>{undoable.message}</span>
            <Button size="sm" variant="ghost" data-testid="recall-undo-do" onClick={undo}>Undo</Button>
            <Button size="sm" variant="ghost" aria-label="Dismiss" onClick={() => setUndoable(null)}>
              <X size={14} />
            </Button>
          </div>
        ) : (
          <>
            {/* The hints the design asks for, as text rather than as a tooltip:
                a keyboard affordance a keyboard user has to hover to discover
                is not an affordance. */}
            <p className={s.hints} aria-hidden="true">
              <kbd>↑</kbd><kbd>↓</kbd> move · <kbd>⏎</kbd> open · <kbd>Esc</kbd> close
            </p>
            <Button size="sm" variant="ghost" className={s.clear} data-testid="recall-clear"
              disabled={!recentsReady() || rawRecents.length === 0} onClick={forgetAll}>
              Clear recents
            </Button>
          </>
        )}
      </footer>
    </div>
  )
}
